import { useState, type ReactNode } from 'react'
import { Box, Button, Divider, Stack, Typography } from '@mui/material'
import RedeemOutlinedIcon from '@mui/icons-material/RedeemOutlined'
import { useGetLojaAtualQuery, useGetMeuCashbackQuery, type Usuario } from '../../api'
import { DialogCashback } from '../../components/dialog-cashback'
import { formatarBRL } from '../../lib/formato'

interface CartaoCashbackProps {
  /** Perfil do cliente logado: preenche o cadastro no cashback. */
  usuario: Pick<Usuario, 'nome' | 'cpf' | 'telefone'>
}

/**
 * Cashback do cliente em "Minha conta": o saldo nesta loja, ou o convite para se cadastrar.
 *
 * Só aparece em loja que aceita cashback como desconto e com o cashback respondendo. Fora isso
 * não renderiza nada, nem o divisor: a tela fica como era antes.
 */
export function CartaoCashback({ usuario }: CartaoCashbackProps): JSX.Element | null {
  const { data: loja } = useGetLojaAtualQuery()
  const lojaAceitaCashback = loja?.configuracao.cashbackUsoAtivo === true
  // Loja com o uso desligado nem consulta o saldo.
  const { data: cashback, isError } = useGetMeuCashbackQuery(undefined, { skip: !lojaAceitaCashback })
  const [cadastroAberto, setCadastroAberto] = useState(false)

  // O diálogo fica fora das condições do cartão: depois do cadastro o cartão troca de conteúdo
  // (ou some, se o cashback sair do ar) e a mensagem de resultado ainda está na tela.
  const dialogoDeCadastro = cadastroAberto ? (
    <DialogCashback usuario={usuario} aoFechar={() => setCadastroAberto(false)} />
  ) : null

  // Enquanto carrega, `cashback` não existe: nada aparece, em vez de um convite que pode virar saldo.
  const mostrarCartao = lojaAceitaCashback && !isError && cashback?.disponivel === true
  if (!mostrarCartao) return dialogoDeCadastro

  return (
    <>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Cashback
      </Typography>
      {cashback.encontrado ? (
        <SaldoDeCashback saldo={cashback.saldoDisponivel} />
      ) : (
        <ConviteDeCadastro aoCadastrar={() => setCadastroAberto(true)} />
      )}
      <Divider sx={{ my: 3 }} />
      {dialogoDeCadastro}
    </>
  )
}

function SaldoDeCashback({ saldo }: { saldo: number }): JSX.Element {
  const temSaldo = saldo > 0

  return (
    <Painel>
      <Typography variant="caption" color="text.secondary" display="block">
        Seu cashback nesta loja
      </Typography>
      <Typography variant="h6">{formatarBRL(saldo)}</Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {temSaldo
          ? 'Use como desconto no seu próximo pedido.'
          : 'Quando você ganhar cashback nesta loja, o valor aparece aqui.'}
      </Typography>
    </Painel>
  )
}

function ConviteDeCadastro({ aoCadastrar }: { aoCadastrar: () => void }): JSX.Element {
  return (
    <Painel>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        Já ganhou cashback nesta loja? Cadastre-se para usar como desconto nos seus pedidos.
      </Typography>
      <Button variant="outlined" onClick={aoCadastrar}>
        Cadastrar no cashback
      </Button>
    </Painel>
  )
}

/** Mesmo painel dos dados pessoais, logo acima na tela. */
function Painel({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
      <RedeemOutlinedIcon sx={{ color: 'primary.main', flexShrink: 0 }} />
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Stack>
  )
}
